<script lang="ts">
  // Quick Actions panel per INTERFACE-SPECIFICATION.md §3.3.
  //
  // The full card library filtered by the authenticated user's
  // affordances. Cards with an active count (e.g. "2 changes are
  // queued for your review") rank to the top per §3.3 ranking
  // rule 1. The panel caps at six cards visible at once; the
  // remaining matches collapse behind an "Open the full Guide →"
  // footer link.
  //
  // Each card carries a Start link (navigates to the route the
  // scenario lives at) and a "Read the guide" link (opens the
  // corresponding walkthrough in the Guide tab). Card titles use
  // the plain-language vocabulary the spec calls for; "cube",
  // "manifest", and "surface projection" do not appear here.

  import { onMount } from 'svelte';
  import { identity } from '../lib/identity.svelte';
  import { api } from '../lib/api';

  interface ActionCard {
    title: string;
    body: string;
    href: string;
    guide_slug?: string;
    show: boolean;
    active_count?: number;
  }

  let pendingReviewCount = $state<number>(0);
  let pendingReviewLoaded = $state<boolean>(false);

  onMount(async () => {
    // The reviewer's queue count is what makes a Quick Action card
    // promote to the top of the panel. Fetch once on mount; the
    // user picks up freshness through their normal navigation.
    // Errors are silent — the card is still useful without a count.
    try {
      if (identity.affordances.canReviewAnything) {
        const open = await api.listProposals('open');
        const reviewerRoles = new Set(
          identity.roles.filter((r) => r.startsWith('reviewer:'))
        );
        pendingReviewCount = open.filter((p) => {
          if (!p.value_type) return false;
          return reviewerRoles.has(`reviewer:${p.value_type}`);
        }).length;
      }
    } catch {
      // Network failure or unauthenticated state — the card still
      // renders without a count and the user can click through.
    } finally {
      pendingReviewLoaded = true;
    }
  });

  const cards = $derived<ActionCard[]>(buildCards());

  function buildCards(): ActionCard[] {
    const a = identity.affordances;
    const out: ActionCard[] = [];

    if (a.canProposeRates) {
      out.push({
        title: 'Update a rate that is currently being served',
        body: 'For ALCO rate reviews and competitor-driven repricing.',
        href: '/catalogue/detail',
        guide_slug: 'update-a-live-rate',
        show: true
      });
      out.push({
        title: 'Update rates across all four products together',
        body: 'For coordinated AU and NZ rate moves under one substantiation.',
        href: '/catalogue',
        guide_slug: 'update-rates-across-all-products',
        show: true
      });
      out.push({
        title: 'Revert a rate to what it was previously',
        body: 'Full or partial — pick specific cells or the whole version.',
        href: '/history',
        guide_slug: 'revert-a-rate',
        show: true
      });
    }
    if (a.canProposeFees) {
      out.push({
        title: 'Adjust an establishment fee schedule',
        body: 'For tier or override changes per ALCO direction.',
        href: '/catalogue/detail',
        guide_slug: 'adjust-an-establishment-fee',
        show: true
      });
    }
    if (a.canProposePurposes) {
      out.push({
        title: 'Add a new loan purpose',
        body: 'Marketing-led purpose addition for a product.',
        href: '/catalogue/detail',
        guide_slug: 'add-a-loan-purpose',
        show: true
      });
      out.push({
        title: 'Remove or rename an existing loan purpose',
        body: 'Retire or relabel a purpose customers can select.',
        href: '/catalogue/detail',
        guide_slug: 'remove-a-loan-purpose',
        show: true
      });
    }
    if (identity.roles.includes('proposer:methodology')) {
      out.push({
        title: 'Change the comparison-rate formula or divisor',
        body: 'Methodology parameter change. Affects every product.',
        href: '/catalogue/detail',
        guide_slug: 'change-the-comparison-rate-formula',
        show: true
      });
    }
    if (a.canReviewAnything) {
      out.push({
        title: 'Review a pending change waiting on me',
        body: pendingReviewLoaded && pendingReviewCount > 0
          ? `${pendingReviewCount} change${pendingReviewCount === 1 ? '' : 's'} ready for your review.`
          : 'Open the queue of proposals ready for review.',
        href: '/changes/pending-review',
        guide_slug: 'review-a-pending-change',
        active_count: pendingReviewCount > 0 ? pendingReviewCount : undefined,
        show: true
      });
      out.push({
        title: 'See what is queued to take effect',
        body: 'Approved changes scheduled for a future effective-from.',
        href: '/changes/approved-queued',
        show: true
      });
    }
    if (a.canManageAccessControl) {
      out.push({
        title: 'Add a person, remove a person, change roles',
        body: 'Authority Register management. Admin only.',
        href: '/governance/authority-register',
        guide_slug: 'add-a-new-person',
        show: true
      });
      out.push({
        title: 'Add a new bureau or update a band structure',
        body: 'Engineering-led; the walkthrough explains the path.',
        href: '/guide/add-a-bureau-or-band-structure',
        guide_slug: 'add-a-bureau-or-band-structure',
        show: true
      });
    }
    if (a.canGenerateComplianceReport) {
      out.push({
        title: 'See what changed last quarter',
        body: 'Quarterly change summary report.',
        href: '/governance/compliance-reports',
        guide_slug: 'generate-a-compliance-report',
        show: true
      });
      out.push({
        title: 'Generate a compliance report',
        body: 'Quarterly change summary, declared asymmetries, point-in-time comparison.',
        href: '/governance/compliance-reports',
        guide_slug: 'generate-a-compliance-report',
        show: true
      });
    }
    if (a.canAuditByHash) {
      out.push({
        title: 'Look up what rate a customer was shown on a date',
        body: 'Reproduces the exact governed state for a customer or regulator response.',
        href: '/governance/audit',
        guide_slug: 'audit-a-historical-rate',
        show: true
      });
    }

    // Ranking per spec §3.3: cards with active counts first, then
    // common-case cards (the order they were pushed above already
    // reflects the proposer-before-reviewer-before-viewer common-case
    // ordering). Cap at six visible.
    return out
      .filter((c) => c.show)
      .sort((a, b) => {
        const aActive = a.active_count ? 1 : 0;
        const bActive = b.active_count ? 1 : 0;
        return bActive - aActive;
      })
      .slice(0, 6);
  }

  const roleSummary = $derived(identity.roles.join(' · '));

  let hidden = $state(false);
</script>

{#if cards.length > 0 && !hidden}
<section class="quick-actions card">
  <header class="quick-actions__header">
    <div>
      <h3>What would you like to do today?</h3>
      {#if roleSummary}
        <p class="muted quick-actions__role-summary">
          Showing actions available with your role: {roleSummary}
        </p>
      {/if}
    </div>
    <button class="btn-link" type="button" onclick={() => (hidden = true)} aria-label="Hide quick actions">Hide ×</button>
  </header>
  <div class="quick-actions__grid">
    {#each cards as card (card.title)}
      <article class="quick-action">
        <strong>{card.title}</strong>
        {#if card.active_count !== undefined}
          <span class="quick-action__count">● {card.body}</span>
        {:else}
          <span class="muted">{card.body}</span>
        {/if}
        <div class="quick-action__cta">
          <a class="quick-action__start" href={card.href}>Start →</a>
          {#if card.guide_slug}
            <a class="quick-action__guide" href="/guide/{card.guide_slug}">Read the guide</a>
          {/if}
        </div>
      </article>
    {/each}
  </div>
  <p class="muted quick-actions__footer">
    Not seeing what you need? <a href="/guide">Open the full Guide →</a>
  </p>
</section>
{/if}

<style>
  .quick-actions { display: flex; flex-direction: column; gap: var(--space-3); }
  .quick-actions__header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--space-3);
  }
  .quick-actions__role-summary {
    margin: var(--space-1) 0 0;
    font-size: var(--type-xs);
    font-family: var(--font-mono);
  }
  .quick-actions__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: var(--space-3);
  }
  .quick-action {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-4);
    border: 1px solid var(--surface-line-soft);
    border-radius: var(--radius-sm);
    background: var(--surface-bg);
  }
  .quick-action__count {
    color: var(--accent-info);
    font-weight: 600;
  }
  .quick-action__cta {
    display: flex;
    gap: var(--space-3);
    align-items: center;
    margin-top: var(--space-1);
  }
  .quick-action__start {
    color: var(--accent-info);
    font-size: var(--type-sm);
    font-weight: 600;
    text-decoration: none;
  }
  .quick-action__guide {
    font-size: var(--type-sm);
    color: var(--surface-muted);
  }
  .quick-actions__footer {
    margin: 0;
    text-align: right;
    font-size: var(--type-sm);
  }

  /* On phone width, single-column the card grid and reflow the
   * header so the role summary keeps its full text and the Hide
   * button does not crowd the heading. */
  @media (max-width: 768px) {
    .quick-actions__header {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--space-2);
    }
    .quick-actions__grid {
      grid-template-columns: 1fr;
    }
    .quick-action__cta {
      flex-wrap: wrap;
    }
    .quick-actions__footer { text-align: left; }
  }
</style>
